import React, { createContext, useContext, useState } from 'react';

const DamageAnimationContext = createContext(null);

/**
 * Provider for damage animation tracking
 * Tracks which units have animations playing
 */
export const DamageAnimationProvider = ({ children }) => {
  const [animationsPlaying, setAnimationsPlaying] = useState(new Set());
  // Track slot-based death animations: "zone_index" -> animations array
  const [slotAnimations, setSlotAnimations] = useState(new Map());

  const registerAnimation = React.useCallback((instanceId) => {
    setAnimationsPlaying(prev => {
      // Only update if instanceId is not already in set
      if (prev.has(instanceId)) {
        return prev;
      }
      return new Set([...prev, instanceId]);
    });
  }, []);

  const unregisterAnimation = React.useCallback((instanceId) => {
    setAnimationsPlaying(prev => {
      // Only update if instanceId is in set
      if (!prev.has(instanceId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(instanceId);
      return next;
    });
  }, []);

  const hasAnimationsPlaying = (instanceId) => {
    return animationsPlaying.has(instanceId);
  };

  // Register slot-based death animations (not linked to unit instance)
  const registerSlotAnimation = React.useCallback((zoneName, index, animations) => {
    const slotKey = `${zoneName}_${index}`;
    setSlotAnimations(prev => {
      const next = new Map(prev);
      next.set(slotKey, animations);
      return next;
    });
  }, []);

  // Unregister slot animation when complete
  const unregisterSlotAnimation = React.useCallback((zoneName, index) => {
    const slotKey = `${zoneName}_${index}`;
    setSlotAnimations(prev => {
      if (!prev.has(slotKey)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(slotKey);
      return next;
    });
  }, []);

  // Get slot animations for a specific slot
  const getSlotAnimations = React.useCallback((zoneName, index) => {
    const slotKey = `${zoneName}_${index}`;
    return slotAnimations.get(slotKey) || null;
  }, [slotAnimations]);

  return (
    <DamageAnimationContext.Provider
      value={{
        registerAnimation,
        unregisterAnimation,
        hasAnimationsPlaying,
        animationsPlaying,
        registerSlotAnimation,
        unregisterSlotAnimation,
        getSlotAnimations,
        slotAnimations,
      }}
    >
      {children}
    </DamageAnimationContext.Provider>
  );
};

export const useDamageAnimationContext = () => {
  const context = useContext(DamageAnimationContext);
  if (!context) {
    // Return no-op functions if context not available
    return {
      registerAnimation: () => {},
      unregisterAnimation: () => {},
      hasAnimationsPlaying: () => false,
      animationsPlaying: new Set(),
      registerSlotAnimation: () => {},
      unregisterSlotAnimation: () => {},
      getSlotAnimations: () => null,
      slotAnimations: new Map(),
    };
  }
  return context;
};
